import { PageLayout } from '@/layouts/page-layout'
import { GroupInput } from '@/components/v2/group-input'
import { Form, Formik } from 'formik'
import { Input, Button } from '@material-tailwind/react'
import { Link, useLoaderData, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AddressForm } from '@/components/address-form'
import { useEffect, useState } from 'react'
import { StatusSwitcher } from '@/components/status-switcher'
import { ConfirmationModal } from '@/components/confirmation-modal'
import { useGetOfficeQuery, useUpdateOfficeMutation } from '@/store/services'
import { list } from '@/router/utils'
import { useAuthorization } from '@/hooks'
import { HasAccess } from '@permify/react-role'

export const OfficeEdit = () => {
  const params = useParams()
  const { hasAccess } = useAuthorization({
    redirection: {
      enable: true
    }
  })
  const [updateOffice, { isLoading }] = useUpdateOfficeMutation()
  const [citiesData] = useLoaderData()
  const { data: { office: currentOffice } } = useGetOfficeQuery(params.id)

  const [openModal, setOpenModal] = useState(false)
  const {
    data: { cities }
  } = citiesData
  const navigate = useNavigate()
  const { t } = useTranslation()

  const initialValues = {
    id: currentOffice.id,
    name: currentOffice.name,
    address_attributes: {
      street: currentOffice.address.street,
      state_id: currentOffice.address.state.id
    },
    person_in_charge: {
      full_name: currentOffice.personInCharge.full_name,
      email: currentOffice.personInCharge.email
    }
  }

  // states

  const onSubmit = async (params) => {
    try {
      await updateOffice(params).unwrap()
      navigate(list('offices'))
    } catch (error) {
      console.log('error', error)
    }
  }

  // effects

  const handleStatus = async () => {
    try {
      await updateOffice({
        id: currentOffice.id,
        status: currentOffice.status === 'active' ? 'inactive' : 'active'
      }).unwrap()
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    hasAccess('office#edit')
  }, [])

  return (
    <PageLayout
      title={t('actions.edit.with', {
        what: t('office.one').toLowerCase()
      })}
      editMode={true}
      rightSection={
        <HasAccess permissions='office#activate'>
          <StatusSwitcher
            label={'office'}
            type={'status'}
            value={currentOffice.status}
            onChange={() => setOpenModal(true)}
          />
        </HasAccess>
      }
    >
      <ConfirmationModal
        onConfirm={handleStatus}
        open={openModal}
        handler={() => setOpenModal(!open)}
        title={t(`modal.confirm.status.${currentOffice.status}.title`)}
        message={t(`modal.confirm.status.${currentOffice.status}.message`)}
      />
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({ values, handleChange }) => (
          <Form>
            <GroupInput divider={false} title='General' description='Rellena con la información de a dependencia y su responsable'>
              <div className='grid grid-cols-2 gap-4'>
                <Input
                  required
                  name='name'
                  label='Nombre de la dependencia'
                  value={values.name}
                  onChange={handleChange}
                />

                <AddressForm
                  required
                  editMode={true}
                  cities={cities}
                  currentResource={currentOffice || {}}
                />

                <Input
                  name='person_in_charge.full_name'
                  label={t('office.personInCharge.fullName')}
                  value={values.person_in_charge.full_name}
                  onChange={handleChange}
                />

                <Input
                  name='person_in_charge.email'
                  label={t('office.personInCharge.email')}
                  value={values.person_in_charge.email}
                  onChange={handleChange}
                />
              </div>
            </GroupInput>

            <div className='flex justify-end gap-3 mt-10'>
              <Link to={list('offices')}>
                <Button variant='text' type='submit'>
                  {t('actions.cancel')}
                </Button>
              </Link>

              <HasAccess permissions='office#update'>
                <Button type='submit' loading={isLoading}>
                  {t('actions.update.one')}
                </Button>
              </HasAccess>
            </div>
          </Form>
        )}
      </Formik>
    </PageLayout>
  )
}
